import React, { useState, useEffect, useRef } from 'react';
import Tool, { type ToolButtonComponent } from './Tool';
import ToolButton from '../toolbars/ToolButton';
import { ApplicationView } from '@aha-app/mvc';
import { ColorPalette, OpacitySlider } from './ColorSwatch';
import { colorPaletteForText } from '../utils/DefaultsManager';
import styles from '../styles.module.css';
import { type Instance as TippyInstance } from 'tippy.js/index';
import type EmbeddedTextBehaviors from '../objects/EmbeddedTextBehaviorsV2';
import { hasEmbeddedTextBehaviors } from '../objects/guards';
import { tools, AhaEditorUtils } from '@aha-app/aha-editor-v2';
import { getTextColorFromSelection } from '../utils/getTextColor';

export function applyTextColor(
  selection: fabric.Object & EmbeddedTextBehaviors,
  color: string,
  operateOnAll = false
) {
  selection.applyTextProperty(editor => {
    AhaEditorUtils.Marks.add(editor, tools.ToolTextColor.mark, color);
  }, operateOnAll);
}

const Popup: React.FC<{
  currentColor: string;
  setCurrentColor: (c: string) => void;
  selection: fabric.Object & EmbeddedTextBehaviors;
  hidePopup: () => void;
  textOpacity: number;
}> = ApplicationView(
  ({ currentColor, setCurrentColor, selection, hidePopup, textOpacity }) => {
    const handleOpacityChange = (opacity: number) => {
      selection.setOnObjects('textOpacity', opacity);
      selection.applyTextProperty(() => ({}), true);
    };

    return (
      <ColorPalette
        palette={colorPaletteForText()}
        currentColor={currentColor}
        setCurrentColor={(color: string) => {
          setCurrentColor(color);
          selection.setOnObjects('textColor', color);
          applyTextColor(selection, color);
        }}
        hidePopup={hidePopup}
      >
        <OpacitySlider
          opacity={textOpacity}
          handleOpacityChange={handleOpacityChange}
        />
      </ColorPalette>
    );
  }
);
const TextButton: ToolButtonComponent = ApplicationView(props => {
  let { selection, tool } = props;
  const tippyRef = useRef<TippyInstance>();
  if (
    hasEmbeddedTextBehaviors(selection) &&
    selection.hasTextFields &&
    'activeTextField' in selection &&
    selection.activeTextField
  ) {
    selection = selection.activeTextField;
  }

  const textFillColor = getTextColorFromSelection(selection);
  const textOpacity = selection.firstObject().get('textOpacity') || 1;
  const [currentColor, setCurrentColor] = useState(textFillColor);

  // Update color if the props change.
  useEffect(() => {
    setCurrentColor(textFillColor);
  }, [textFillColor]);

  // Watch for text caret updates, text might be multi-coloured
  useEffect(() => {
    function onSelectionChange() {
      setTimeout(() => {
        const textFillColor = getTextColorFromSelection(selection);
        setCurrentColor(textFillColor);
      }, 1);
    }
    document.addEventListener('selectionchange', onSelectionChange);
    return () =>
      document.removeEventListener('selectionchange', onSelectionChange);
  }, []);

  return (
    <ToolButton
      popup={
        <Popup
          currentColor={currentColor}
          textOpacity={textOpacity}
          setCurrentColor={setCurrentColor}
          selection={selection as fabric.Object & EmbeddedTextBehaviors}
          hidePopup={() => tippyRef.current?.hide()}
        />
      }
      tippyRef={tippyRef}
      tool={tool}
    >
      <div className={styles.textToolButton}>
        <aha-icon icon='fa-solid fa-font' />
        <div
          data-testid='Tool-text-color-swatch'
          className={styles.textToolButtonSwatch}
          style={{ backgroundColor: currentColor }}
        />
      </div>
    </ToolButton>
  );
});

class ToolTextColor extends Tool {
  static toolName = 'Text color';

  static buttonComponent() {
    return TextButton;
  }
}

export default ToolTextColor;
